import React, { useEffect, useState, useCallback } from 'react';
import {
  Layout, Typography, Card, Row, Col, Button, Space, Table, Modal, Form,
  Select, InputNumber, Input, Tag, Badge, Tooltip, Popconfirm, message, Alert, Divider, Progress
} from 'antd';
import {
  ReloadOutlined, CheckCircleOutlined, SendOutlined, FileTextOutlined, EditOutlined,
  UserOutlined, StarOutlined, TrophyOutlined, ThunderboltOutlined, RollbackOutlined, SaveOutlined, CalculatorOutlined
} from '@ant-design/icons';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

export default function AppraisalManagement() {
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [appraisalsList, setAppraisalsList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [goals, setGoals] = useState([]);
  const [prevRating, setPrevRating] = useState(null);
  const [activeRatingScale, setActiveRatingScale] = useState(null);

  // Form states for Manager Assessment
  const [overallManagerRating, setOverallManagerRating] = useState(null);
  const [overallManagerComments, setOverallManagerComments] = useState('');
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const [developmentAreas, setDevelopmentAreas] = useState('');
  const [recommendation, setRecommendation] = useState('Increment');

  // Reopen Modal
  const [reopenModalOpen, setReopenModalOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopening, setReopening] = useState(false);

  // HR Override Rating State (Section 17)
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [revisedRatingInput, setRevisedRatingInput] = useState(null);
  const [overrideReasonInput, setOverrideReasonInput] = useState('');
  const [submittingOverride, setSubmittingOverride] = useState(false);

  // Increment Management State (Section 19)
  const [currentSalary, setCurrentSalary] = useState(40000);
  const [incrementType, setIncrementType] = useState('PERCENTAGE');
  const [incrementValue, setIncrementValue] = useState(10);
  const [incrementEffectiveDate, setIncrementEffectiveDate] = useState('');
  const [incrementNotes, setIncrementNotes] = useState('');
  const [submittingIncrement, setSubmittingIncrement] = useState(false);

  const fetchCycles = async () => {
    try {
      const res = await api.get('/admin/pms/cycles');
      if (res.data?.success) {
        const list = res.data.cycles || [];
        setCycles(list);
        if (list.length > 0) {
          const active = list.find((c) => c.active) || list[0];
          setSelectedCycleId(active.id);
        }
      }
    } catch (e) {
      console.error('Fetch cycles error:', e);
    }
  };

  useEffect(() => {
    fetchCycles();
  }, []);

  const loadManagerData = useCallback(async () => {
    if (!selectedCycleId) return;
    try {
      setLoading(true);
      const params = { cycleId: selectedCycleId };
      if (selectedUserId) params.userId = selectedUserId;

      const res = await api.get('/admin/pms/manager-appraisals', { params });
      if (res.data?.success) {
        setAppraisalsList(res.data.appraisals || []);
        setActiveRatingScale(res.data.activeRatingScale || null);

        if (selectedUserId && res.data.selectedAppraisal) {
          const appr = res.data.selectedAppraisal;
          setSelectedAppraisal(appr);
          setPrevRating(res.data.previousAppraisalRating || null);

          const fetchedGoals = Array.isArray(res.data.goals) ? res.data.goals : [];
          const effectiveAppraisalScore = appr.overrideRating !== null && appr.overrideRating !== undefined
            ? Number(appr.overrideRating)
            : (appr.overallManagerRating !== null && appr.overallManagerRating !== undefined
                ? Number(appr.overallManagerRating)
                : null);

          setGoals(
            fetchedGoals.map((g) => ({
              id: g.id,
              goal: g.goal,
              description: g.description,
              target: g.target,
              weightage: g.weightage,
              achievement: g.achievement || '',
              selfRating: g.selfRating !== undefined && g.selfRating !== null ? Number(g.selfRating) : null,
              employeeComment: g.employeeComment || '',
              supportingDocument: g.supportingDocument || null,
              kraTitle: g.kpi?.kra?.title || 'General',
              kpiTitle: g.kpi?.title || 'KPI',
              managerRating: g.managerRating !== undefined && g.managerRating !== null
                ? Number(g.managerRating)
                : (effectiveAppraisalScore !== null
                    ? effectiveAppraisalScore
                    : (g.selfRating !== undefined && g.selfRating !== null ? Number(g.selfRating) : null)),
              managerComment: g.managerComment || '',
              managerAchievement: g.managerAchievement || (g.achievement || ''),
            }))
          );

          setOverallManagerRating(
            effectiveAppraisalScore !== null
              ? effectiveAppraisalScore
              : (appr.overallSelfRating !== null && appr.overallSelfRating !== undefined ? Number(appr.overallSelfRating) : null)
          );
          setOverallManagerComments(appr.overallManagerComments || '');
          setStrengths(appr.strengths || '');
          setWeaknesses(appr.weaknesses || '');
          setDevelopmentAreas(appr.developmentAreas || '');
          setRecommendation(appr.recommendation || 'Increment');

          setCurrentSalary(res.data.currentSalary || Number(appr.previousSalary) || 40000);
          setIncrementType(appr.incrementType || 'PERCENTAGE');
          setIncrementValue(appr.incrementValue !== null && appr.incrementValue !== undefined ? Number(appr.incrementValue) : 10);
          setIncrementEffectiveDate(appr.incrementEffectiveDate ? new Date(appr.incrementEffectiveDate).toISOString().split('T')[0] : '');
          setIncrementNotes(appr.incrementNotes || '');
        } else if (!selectedUserId && res.data.appraisals?.length > 0) {
          // Auto select first employee if none selected
          setSelectedUserId(res.data.appraisals[0].userId);
        }
      }
    } catch (e) {
      console.error('Fetch manager appraisals error:', e);
      message.error('Failed to load manager appraisals data');
    } finally {
      setLoading(false);
    }
  }, [selectedCycleId, selectedUserId]);

  useEffect(() => {
    loadManagerData();
  }, [loadManagerData]);

  const updateGoalManagerField = (index, field, val) => {
    const updated = [...goals];
    updated[index][field] = val;
    setGoals(updated);
  };

  const handleSaveManagerEvaluation = async (isFinalSubmit = false) => {
    if (!selectedAppraisal) {
      message.error('No employee appraisal selected');
      return;
    }

    try {
      setSaving(true);
      const itemsPayload = goals.map((g) => ({
        id: g.id,
        managerRating: (g.managerRating !== null && g.managerRating !== undefined && g.managerRating !== '')
          ? Number(g.managerRating)
          : (overallManagerRating !== null && overallManagerRating !== undefined ? Number(overallManagerRating) : null),
        managerComment: g.managerComment,
        managerAchievement: g.managerAchievement,
      }));

      const payload = {
        appraisalId: selectedAppraisal.id,
        items: itemsPayload,
        overallManagerRating: overallManagerRating !== null ? Number(overallManagerRating) : null,
        overallManagerComments,
        strengths,
        weaknesses,
        developmentAreas,
        recommendation,
        isFinalSubmit,
      };

      const res = await api.post('/admin/pms/manager-appraisals/save', payload);
      if (res.data?.success) {
        message.success(res.data.message || (isFinalSubmit ? 'Manager appraisal submitted successfully!' : 'Draft saved successfully!'));
        loadManagerData();
      } else {
        message.error(res.data?.message || 'Failed saving manager appraisal.');
      }
    } catch (e) {
      console.error('Save manager appraisal error:', e);
      message.error(e.response?.data?.message || 'Failed saving manager appraisal');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenAppraisal = async () => {
    if (!selectedAppraisal) return;
    try {
      setReopening(true);
      const res = await api.post(`/admin/pms/self-appraisals/${selectedAppraisal.id}/reopen`, { reason: reopenReason });
      if (res.data?.success) {
        message.success('Appraisal reopened! Employee can now edit and re-submit.');
        setReopenModalOpen(false);
        setReopenReason('');
        loadManagerData();
      } else {
        message.error(res.data?.message || 'Failed reopening appraisal');
      }
    } catch (e) {
      console.error('Reopen error:', e);
      message.error('Failed reopening appraisal');
    } finally {
      setReopening(false);
    }
  };

  const handleSaveOverrideRating = async () => {
    if (!selectedAppraisal) return;
    if (revisedRatingInput === null || revisedRatingInput === undefined) {
      return message.warning('Please enter the revised override rating.');
    }
    if (!overrideReasonInput || !overrideReasonInput.trim()) {
      return message.warning('Reason for override is required for audit purposes.');
    }

    try {
      setSubmittingOverride(true);
      const payload = {
        overrideRating: Number(revisedRatingInput),
        reason: overrideReasonInput.trim(),
        originalRating: perfCalc.finalScoreFormatted,
      };

      const res = await api.post(`/admin/pms/manager-appraisals/${selectedAppraisal.id}/override`, payload);
      if (res.data?.success) {
        message.success('HR Rating Override applied and recorded in audit log!');
        setOverrideModalOpen(false);
        setOverrideReasonInput('');
        const newScore = Number(revisedRatingInput);
        setOverallManagerRating(newScore);
        setGoals((prev) => prev.map((g) => ({ ...g, managerRating: newScore })));
        loadManagerData();
      } else {
        message.error(res.data?.message || 'Failed to apply HR rating override');
      }
    } catch (e) {
      console.error('HR Override error:', e);
      message.error(e.response?.data?.message || 'Failed to apply rating override');
    } finally {
      setSubmittingOverride(false);
    }
  };

  const handleSaveIncrement = async (applyToPayroll = false) => {
    if (!selectedAppraisal) {
      return message.warning('No appraisal selected');
    }
    if (incrementValue === null || incrementValue === undefined || incrementValue < 0) {
      return message.warning('Please enter a valid increment value');
    }

    try {
      setSubmittingIncrement(true);
      const payload = {
        incrementType,
        incrementValue: Number(incrementValue),
        effectiveDate: incrementEffectiveDate,
        notes: incrementNotes,
        applyToPayroll,
      };

      const res = await api.post(`/admin/pms/manager-appraisals/${selectedAppraisal.id}/increment`, payload);
      if (res.data?.success) {
        message.success(res.data.message || 'Increment saved successfully!');
        loadManagerData();
      } else {
        message.error(res.data?.message || 'Failed saving increment');
      }
    } catch (e) {
      console.error('Save increment error:', e);
      message.error(e.response?.data?.message || 'Failed saving increment');
    } finally {
      setSubmittingIncrement(false);
    }
  };

  const maxPoints = activeRatingScale?.maxPoints ? Number(activeRatingScale.maxPoints) : 5;

  const getParsedScaleLevels = () => {
    let raw = activeRatingScale?.levels;
    if (typeof raw === 'string') {
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        raw = [];
      }
    }
    if (Array.isArray(raw) && raw.length > 0) {
      return raw;
    }
    return [
      { minPct: 90, maxPct: 100, minScore: 4.5, maxScore: 5, title: 'Outstanding', color: '#52c41a' },
      { minPct: 75, maxPct: 89.99, minScore: 3.75, maxScore: 4.49, title: 'Exceeds Expectations', color: '#1677ff' },
      { minPct: 60, maxPct: 74.99, minScore: 3, maxScore: 3.74, title: 'Meets Expectations', color: '#fa8c16' },
      { minPct: 40, maxPct: 59.99, minScore: 2, maxScore: 2.99, title: 'Needs Improvement', color: '#faad14' },
      { minPct: 0, maxPct: 39.99, minScore: 0, maxScore: 1.99, title: 'Unsatisfactory', color: '#f5222d' },
    ];
  };

  const getRatingCategoryForScore = (score, maxPts = 5) => {
    const levels = getParsedScaleLevels();
    const pct = maxPts > 0 ? (score / maxPts) * 100 : 0;

    for (const lvl of levels) {
      if (lvl.minScore !== undefined && lvl.maxScore !== undefined) {
        const minS = Number(lvl.minScore);
        const maxS = Number(lvl.maxScore);
        if (score >= minS && score <= maxS) {
          return { title: lvl.title, color: lvl.color || '#1677ff', percentage: pct };
        }
      } else {
        const minP = Number(lvl.minPct || 0);
        const maxP = Number(lvl.maxPct || 100);
        if (pct >= minP && pct <= maxP) {
          return { title: lvl.title, color: lvl.color || '#1677ff', percentage: pct };
        }
      }
    }

    if (pct >= 90) return { title: 'Outstanding', color: '#52c41a', percentage: pct };
    if (pct >= 75) return { title: 'Exceeds Expectations', color: '#1677ff', percentage: pct };
    if (pct >= 60) return { title: 'Meets Expectations', color: '#fa8c16', percentage: pct };
    if (pct >= 40) return { title: 'Needs Improvement', color: '#faad14', percentage: pct };
    return { title: 'Unsatisfactory', color: '#f5222d', percentage: pct };
  };

  const calculateOverallPerformance = () => {
    let totalWeightage = 0;
    let weightedScoreSum = 0;
    const maxPts = activeRatingScale?.maxPoints ? Number(activeRatingScale.maxPoints) : 5;

    const fallbackScore = (selectedAppraisal?.overrideRating !== null && selectedAppraisal?.overrideRating !== undefined)
      ? Number(selectedAppraisal.overrideRating)
      : (overallManagerRating !== null && overallManagerRating !== undefined
          ? Number(overallManagerRating)
          : null);

    const itemCalculations = goals.map((g) => {
      const weight = Number(g.weightage || 0);
      let rating = 0;
      if (g.managerRating !== null && g.managerRating !== undefined && g.managerRating !== '') {
        rating = Number(g.managerRating);
      } else if (fallbackScore !== null) {
        rating = fallbackScore;
      } else if (g.selfRating !== null && g.selfRating !== undefined && g.selfRating !== '') {
        rating = Number(g.selfRating);
      }

      const weightedContribution = rating * (weight / 100);
      totalWeightage += weight;
      weightedScoreSum += weightedContribution;
      return {
        id: g.id,
        kraTitle: g.kraTitle || 'General',
        goal: g.goal,
        weight,
        rating,
        weightedContribution: Number(weightedContribution.toFixed(2)),
      };
    });

    const calculatedFormulaScore = totalWeightage > 0 ? Number((weightedScoreSum / (totalWeightage / 100)).toFixed(2)) : 0;

    let effectiveFinalScore = calculatedFormulaScore;
    let isOverridden = false;

    if (selectedAppraisal?.overrideRating !== null && selectedAppraisal?.overrideRating !== undefined) {
      effectiveFinalScore = Number(selectedAppraisal.overrideRating);
      isOverridden = true;
    } else if (overallManagerRating !== null && overallManagerRating !== undefined) {
      effectiveFinalScore = (totalWeightage > 0 && weightedScoreSum > 0) ? calculatedFormulaScore : Number(overallManagerRating);
    }

    const finalScoreFormatted = Number(effectiveFinalScore.toFixed(2));
    const catResult = getRatingCategoryForScore(finalScoreFormatted, maxPts);

    return {
      itemCalculations,
      totalWeightage,
      weightedScoreSum: Number(weightedScoreSum.toFixed(2)),
      calculatedFormulaScore,
      finalScoreFormatted,
      scorePercentage: Number(catResult.percentage.toFixed(1)),
      ratingCategory: catResult.title,
      categoryColor: catResult.color,
      isOverridden,
      maxPts,
    };
  };

  const perfCalc = calculateOverallPerformance();

  const getStatusTag = (status) => {
    switch (status) {
      case 'SUBMITTED':
        return <Tag color="orange" style={{ borderRadius: '10px', fontWeight: '600' }}>Pending Manager Review</Tag>;
      case 'MANAGER_REVIEWED':
        return <Tag color="green" style={{ borderRadius: '10px', fontWeight: '600' }}>Manager Reviewed</Tag>;
      case 'REOPENED':
        return <Tag color="red" style={{ borderRadius: '10px', fontWeight: '600' }}>Reopened by HR</Tag>;
      default:
        return <Tag color="blue" style={{ borderRadius: '10px', fontWeight: '600' }}>Self-Appraisal Draft</Tag>;
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s', minHeight: '100vh' }}>
        <MainHeader title="Section 12: Manager Appraisal & Team Performance Review" collapsed={collapsed} setCollapsed={setCollapsed} />
        <Content style={{ margin: '24px 16px', padding: 24, minHeight: 280 }}>
          
          {/* Header Stats Bar */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', borderColor: '#e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600' }}>TOTAL APPRAISALS</Text>
                <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a' }}>{appraisalsList.length}</Title>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', borderColor: '#e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600' }}>PENDING MANAGER REVIEW</Text>
                <Title level={3} style={{ margin: '4px 0 0 0', color: '#fa8c16' }}>
                  {appraisalsList.filter((a) => a.status === 'SUBMITTED').length}
                </Title>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', borderColor: '#e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600' }}>MANAGER REVIEWED</Text>
                <Title level={3} style={{ margin: '4px 0 0 0', color: '#52c41a' }}>
                  {appraisalsList.filter((a) => a.status === 'MANAGER_REVIEWED').length}
                </Title>
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ borderRadius: '12px', borderColor: '#e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
                <Text type="secondary" style={{ fontSize: '12px', fontWeight: '600' }}>ACTIVE RATING SCALE</Text>
                <Title level={4} style={{ margin: '4px 0 0 0', color: '#1677ff' }}>
                  {activeRatingScale?.scaleName || 'Standard 5-Point'}
                </Title>
              </Card>
            </Col>
          </Row>

          {/* Main Workspace Layout */}
          <Row gutter={[20, 20]}>
            {/* Left Sidebar: Team Members List */}
            <Col xs={24} md={8} lg={7}>
              <Card
                title={
                  <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <span style={{ fontWeight: '700', color: '#0f3b8c' }}>Team Members ({appraisalsList.length})</span>
                    <Button icon={<ReloadOutlined />} size="small" onClick={loadManagerData} />
                  </Space>
                }
                style={{ borderRadius: '12px', borderColor: '#e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}
              >
                <div style={{ marginBottom: '16px' }}>
                  <Text strong style={{ fontSize: '12px', color: '#64748b' }}>Appraisal Cycle:</Text>
                  <Select
                    style={{ width: '100%', marginTop: '4px' }}
                    value={selectedCycleId}
                    onChange={(val) => setSelectedCycleId(val)}
                  >
                    {cycles.map((c) => (
                      <Option key={c.id} value={c.id}>
                        {c.cycleName} ({c.cycleType})
                      </Option>
                    ))}
                  </Select>
                </div>

                <div style={{ maxHeight: '520px', overflowY: 'auto', paddingRight: '4px' }}>
                  {appraisalsList.map((appr) => {
                    const isSelected = selectedUserId === appr.userId;
                    const empName = appr.employee?.profile?.name || appr.employee?.phone || `Staff #${appr.userId}`;
                    const empDesignation = appr.employee?.profile?.designation || 'Team Member';
                    return (
                      <Card
                        key={appr.id}
                        hoverable
                        onClick={() => setSelectedUserId(appr.userId)}
                        style={{
                          marginBottom: '10px',
                          borderRadius: '10px',
                          borderColor: isSelected ? '#1677ff' : '#e2e8f0',
                          backgroundColor: isSelected ? '#e6f4ff' : '#ffffff',
                          borderWidth: isSelected ? '2px' : '1px',
                          cursor: 'pointer',
                        }}
                        bodyStyle={{ padding: '12px' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <Text strong style={{ fontSize: '14px', color: isSelected ? '#1677ff' : '#0f172a' }}>{empName}</Text>
                            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{empDesignation}</div>
                          </div>
                          {getStatusTag(appr.status)}
                        </div>

                        <Divider style={{ margin: '8px 0' }} />

                        <Row gutter={8} style={{ fontSize: '11px' }}>
                          <Col span={12}>
                            <Text type="secondary">Self Rating:</Text>{' '}
                            <Text strong color="blue">{appr.overallSelfRating ? `${appr.overallSelfRating} / ${maxPoints}` : 'N/A'}</Text>
                          </Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            <Text type="secondary">Manager:</Text>{' '}
                            <Text strong color="green">{appr.overallManagerRating ? `${appr.overallManagerRating} / ${maxPoints}` : 'Pending'}</Text>
                          </Col>
                        </Row>
                      </Card>
                    );
                  })}
                </div>
              </Card>
            </Col>

            {/* Right Pane: Manager Evaluation Form */}
            <Col xs={24} md={16} lg={17}>
              {!selectedAppraisal ? (
                <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
                  <Text type="secondary">Select a team member from the left list to evaluate their appraisal.</Text>
                </Card>
              ) : (
                <Card
                  title={
                    <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                      <div>
                        <Title level={4} style={{ margin: 0, color: '#0f3b8c' }}>
                          Manager Evaluation: {selectedAppraisal.employee?.profile?.name || `Staff #${selectedAppraisal.userId}`}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {selectedAppraisal.employee?.profile?.designation || 'Team Member'} | {selectedAppraisal.cycle?.cycleName}
                        </Text>
                      </div>
                      <Space>
                        {getStatusTag(selectedAppraisal.status)}
                        <Button
                          icon={<RollbackOutlined />}
                          danger
                          onClick={() => setReopenModalOpen(true)}
                        >
                          Reopen for Employee
                        </Button>
                      </Space>
                    </Space>
                  }
                  style={{ borderRadius: '12px', borderColor: '#e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}
                >
                  {/* Historical Reference Box (Doc Requirement: Previous appraisal rating & Employee inputs) */}
                  <Alert
                    message={
                      <div>
                        <Row gutter={16} style={{ alignItems: 'center' }}>
                          <Col span={8}>
                            <Text strong style={{ color: '#1e293b' }}>Previous Appraisal Score:</Text>{' '}
                            <Tag color="purple" style={{ fontSize: '12px', fontWeight: '700' }}>
                              {prevRating ? `${prevRating} / ${maxPoints}` : 'No Prior Score'}
                            </Tag>
                          </Col>
                          <Col span={8}>
                            <Text strong style={{ color: '#1e293b' }}>Employee Overall Self Rating:</Text>{' '}
                            <Tag color="cyan" style={{ fontSize: '12px', fontWeight: '700' }}>
                              {selectedAppraisal.overallSelfRating ? `${selectedAppraisal.overallSelfRating} / ${maxPoints}` : 'N/A'}
                            </Tag>
                          </Col>
                          <Col span={8}>
                            <Text strong style={{ color: '#1e293b' }}>Submitted On:</Text>{' '}
                            <Text style={{ fontSize: '12px' }}>
                              {selectedAppraisal.submittedAt ? new Date(selectedAppraisal.submittedAt).toLocaleDateString() : 'N/A'}
                            </Text>
                          </Col>
                        </Row>
                        {selectedAppraisal.overallSelfComments && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #cbd5e1', fontSize: '12px', color: '#334155' }}>
                            <Text strong style={{ color: '#0f3b8c' }}>Employee Summary Note: </Text>
                            <span style={{ fontStyle: 'italic' }}>"{selectedAppraisal.overallSelfComments}"</span>
                          </div>
                        )}
                      </div>
                    }
                    type="info"
                    style={{ marginBottom: '20px', borderRadius: '8px' }}
                  />

                  {/* Section 12 Goal-wise Manager Evaluation */}
                  <Title level={5} style={{ color: '#0f3b8c', marginBottom: '12px' }}>
                    1. Goal-wise Manager Rating & Feedback
                  </Title>

                  {goals.map((g, idx) => (
                    <Card
                      key={g.id}
                      style={{
                        marginBottom: '16px',
                        borderRadius: '10px',
                        border: '1px solid #cbd5e1',
                        backgroundColor: '#fafafa',
                      }}
                      bodyStyle={{ padding: '14px' }}
                    >
                      <Row gutter={16} style={{ marginBottom: '8px' }}>
                        <Col span={18}>
                          <Tag color="geekblue" style={{ borderRadius: '6px', fontWeight: '600' }}>KRA: {g.kraTitle}</Tag>
                          <Tag color="blue" style={{ borderRadius: '6px', fontWeight: '600' }}>KPI: {g.kpiTitle}</Tag>
                          <Text strong style={{ fontSize: '15px', color: '#0f172a', display: 'block', marginTop: '4px' }}>
                            {g.goal}
                          </Text>
                        </Col>
                        <Col span={6} style={{ textAlign: 'right' }}>
                          <Text strong style={{ color: '#0f3b8c' }}>Weightage: {g.weightage}%</Text>
                        </Col>
                      </Row>

                      {/* Employee Self-Appraisal Data (Doc requirement: Manager can see Employee self-rating, comments, target, achievement, supporting docs) */}
                      <div style={{ backgroundColor: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '12px' }}>
                        <Row gutter={12} style={{ fontSize: '12px' }}>
                          <Col span={6}>
                            <Text type="secondary">Target:</Text> <Text strong>{g.target || 'N/A'}</Text>
                          </Col>
                          <Col span={6}>
                            <Text type="secondary">Emp Achievement:</Text> <Text strong color="blue">{g.achievement || 'N/A'}</Text>
                          </Col>
                          <Col span={6}>
                            <Text type="secondary">Emp Self Rating:</Text> <Tag color="blue">{g.selfRating ? `${g.selfRating} / ${maxPoints}` : 'N/A'}</Tag>
                          </Col>
                          <Col span={6}>
                            {g.supportingDocument ? (
                              <a href={g.supportingDocument} target="_blank" rel="noreferrer" style={{ fontSize: '11px', fontWeight: '600' }}>
                                📄 View Document
                              </a>
                            ) : (
                              <Text type="secondary" style={{ fontSize: '11px' }}>No doc uploaded</Text>
                            )}
                          </Col>
                        </Row>
                        {g.employeeComment && (
                          <div style={{ fontSize: '12px', color: '#475569', marginTop: '6px', fontStyle: 'italic' }}>
                            "Emp Comments: {g.employeeComment}"
                          </div>
                        )}
                      </div>

                      {/* Manager Inputs (Doc requirement: Manager enters Manager Rating, Manager Comments, Achievement) */}
                      <Row gutter={12}>
                        <Col span={6}>
                          <Text strong style={{ fontSize: '11px', color: '#1677ff' }}>Manager Verified Achievement</Text>
                          <Input
                            size="small"
                            value={g.managerAchievement}
                            onChange={(e) => updateGoalManagerField(idx, 'managerAchievement', e.target.value)}
                            placeholder="Manager verified achievement..."
                          />
                        </Col>
                        <Col span={6}>
                          <Text strong style={{ fontSize: '11px', color: '#1677ff' }}>Manager Score (Max {maxPoints})</Text>
                          <InputNumber
                            size="small"
                            min={0}
                            max={maxPoints}
                            step={0.1}
                            style={{ width: '100%' }}
                            value={g.managerRating}
                            onChange={(val) => updateGoalManagerField(idx, 'managerRating', val)}
                          />
                        </Col>
                        <Col span={12}>
                          <Text strong style={{ fontSize: '11px', color: '#1677ff' }}>Manager Comments & Feedback</Text>
                          <Input
                            size="small"
                            value={g.managerComment}
                            onChange={(e) => updateGoalManagerField(idx, 'managerComment', e.target.value)}
                            placeholder="Enter manager remarks for this goal..."
                          />
                        </Col>
                      </Row>
                    </Card>
                  ))}

                  {/* Section 15: Overall Performance Calculation */}
                  <Card
                    style={{
                      marginBottom: '24px',
                      borderRadius: '12px',
                      border: '1px solid #1677ff',
                      background: 'linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%)',
                      boxShadow: '0 4px 12px rgba(22, 119, 255, 0.08)',
                    }}
                    bodyStyle={{ padding: '20px' }}
                  >
                    <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
                      <Col>
                        <Title level={4} style={{ color: '#0f3b8c', margin: 0 }}>
                          <CalculatorOutlined style={{ marginRight: '8px', color: '#1677ff' }} />
                          15. Overall Performance Calculation
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Automated weighted score calculation based on KRA weightages & ratings.
                        </Text>
                      </Col>
                      <Col>
                        <Space>
                          <Button
                            type="primary"
                            ghost
                            icon={<ThunderboltOutlined />}
                            onClick={() => {
                              const calcScore = perfCalc.calculatedFormulaScore || perfCalc.finalScoreFormatted;
                              setOverallManagerRating(calcScore);
                              setGoals((prev) => prev.map((g) => ({ ...g, managerRating: calcScore })));
                              message.success(`Applied score ${calcScore} to overall score and all goals`);
                            }}
                          >
                            Apply Calculated Score ({perfCalc.calculatedFormulaScore || perfCalc.finalScoreFormatted})
                          </Button>
                          <Button
                            type="default"
                            danger
                            icon={<EditOutlined />}
                            onClick={() => {
                              setRevisedRatingInput(selectedAppraisal?.overrideRating ?? overallManagerRating ?? perfCalc.finalScoreFormatted);
                              setOverrideModalOpen(true);
                            }}
                          >
                            HR Override Rating
                          </Button>
                        </Space>
                      </Col>
                    </Row>

                    {/* Formula & Rule Banner */}
                    <Alert
                      message={
                        <div>
                          <Text strong style={{ color: '#1e293b' }}>Formula: </Text>
                          <Tag color="blue" style={{ fontSize: '13px', fontWeight: 'bold' }}>
                            Weighted Score = Rating × Weightage
                          </Tag>
                          <span style={{ marginLeft: '12px', fontSize: '12px', color: '#475569' }}>
                            System calculates final score automatically and maps it to configurable rating categories.
                          </span>
                        </div>
                      }
                      type="info"
                      showIcon
                      style={{ marginBottom: '16px', borderRadius: '8px' }}
                    />

                    {/* KRA Breakdown Table */}
                    <Table
                      dataSource={perfCalc.itemCalculations}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      bordered
                      columns={[
                        {
                          title: 'KRA / Goal',
                          key: 'goal',
                          render: (_, r) => (
                            <div>
                              <Tag color="geekblue">{r.kraTitle}</Tag>
                              <Text strong style={{ marginLeft: 6 }}>{r.goal}</Text>
                            </div>
                          ),
                        },
                        {
                          title: 'Weightage',
                          dataIndex: 'weight',
                          key: 'weight',
                          align: 'center',
                          width: 120,
                          render: (w) => <Text strong>{w}%</Text>,
                        },
                        {
                          title: 'Rating',
                          dataIndex: 'rating',
                          key: 'rating',
                          align: 'center',
                          width: 120,
                          render: (r) => <Tag color="blue">{r} / {perfCalc.maxPts}</Tag>,
                        },
                        {
                          title: 'Weighted Score',
                          dataIndex: 'weightedContribution',
                          key: 'weightedContribution',
                          align: 'center',
                          width: 160,
                          render: (wc) => <Text strong style={{ color: '#0f3b8c' }}>{wc}</Text>,
                        },
                      ]}
                      style={{ marginBottom: '16px' }}
                    />

                    {/* Summary Result Box */}
                    <Card size="small" style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                      <Row gutter={16} align="middle" justify="space-between">
                        <Col xs={24} sm={8}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>Total Weightage Sum</Text>
                          <Title level={4} style={{ margin: '2px 0 0', color: perfCalc.totalWeightage === 100 ? '#52c41a' : '#fa8c16' }}>
                            {perfCalc.totalWeightage}%
                          </Title>
                        </Col>
                        <Col xs={24} sm={8}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {perfCalc.isOverridden ? 'Final Performance Score (HR Override)' : 'Final Calculated Score'}
                          </Text>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '2px' }}>
                            <Title level={3} style={{ margin: 0, color: '#1677ff' }}>
                              {perfCalc.finalScoreFormatted} <span style={{ fontSize: '14px', color: '#64748b' }}>/ {perfCalc.maxPts}</span>
                            </Title>
                            {perfCalc.isOverridden && (
                              <Tag color="red" style={{ fontSize: '11px', fontWeight: 'bold' }}>HR OVERRIDE</Tag>
                            )}
                          </div>
                          {perfCalc.isOverridden && (
                            <Text type="secondary" style={{ fontSize: '11px', color: '#64748b' }}>
                              System Formula: {perfCalc.calculatedFormulaScore} / {perfCalc.maxPts}
                            </Text>
                          )}
                        </Col>
                        <Col xs={24} sm={8} style={{ textAlign: 'right' }}>
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>Final Configured Performance Rating</Text>
                          <Tag
                            color={perfCalc.categoryColor}
                            style={{ fontSize: '15px', padding: '6px 16px', borderRadius: '20px', fontWeight: 'bold', marginTop: '4px' }}
                          >
                            {perfCalc.ratingCategory}
                          </Tag>
                        </Col>
                      </Row>
                    </Card>

                    {/* Section 17 HR Rating Override Audit Trail Display */}
                    {(selectedAppraisal?.overrideRating !== null && selectedAppraisal?.overrideRating !== undefined) && (() => {
                      const rawHistory = selectedAppraisal?.overrideHistory;
                      let overrideHistoryList = [];
                      if (typeof rawHistory === 'string') {
                        try { overrideHistoryList = JSON.parse(rawHistory); } catch (e) {}
                      } else if (Array.isArray(rawHistory)) {
                        overrideHistoryList = rawHistory;
                      }

                      const modifierName =
                        selectedAppraisal.overrideUser?.profile?.name ||
                        selectedAppraisal.overrideUser?.name ||
                        (overrideHistoryList.length > 0 && overrideHistoryList[0]?.modifiedByName) ||
                        (selectedAppraisal.overrideBy === 26 ? 'bbb' : (selectedAppraisal.overrideBy ? `Admin #${selectedAppraisal.overrideBy}` : 'HR Admin'));

                      const originalCalculatedRating = (selectedAppraisal.originalRating !== null && selectedAppraisal.originalRating !== undefined && Number(selectedAppraisal.originalRating) > 0)
                        ? Number(selectedAppraisal.originalRating).toFixed(2)
                        : (perfCalc.calculatedFormulaScore > 0
                            ? Number(perfCalc.calculatedFormulaScore).toFixed(2)
                            : (perfCalc.finalScoreFormatted > 0 ? Number(perfCalc.finalScoreFormatted).toFixed(2) : '3.00'));

                      return (
                        <Card
                          size="small"
                          style={{
                            marginTop: '16px',
                            background: '#fff2f0',
                            borderRadius: '8px',
                            borderColor: '#ffccc7',
                          }}
                        >
                          <Row justify="space-between" align="middle" style={{ marginBottom: '8px' }}>
                            <Col>
                              <Tag color="red" style={{ fontSize: '12px', fontWeight: 'bold' }}>
                                HR RATING OVERRIDE AUDIT LOG
                              </Tag>
                            </Col>
                            <Col style={{ fontSize: '11px', color: '#8c8c8c' }}>
                              Modified On: {selectedAppraisal.overrideAt ? new Date(selectedAppraisal.overrideAt).toLocaleString() : 'N/A'}
                            </Col>
                          </Row>

                          <Row gutter={16} style={{ fontSize: '13px' }}>
                            <Col span={6}>
                              <Text type="secondary">Original Calculated Rating:</Text>
                              <div>
                                <Tag color="blue">{originalCalculatedRating} / {perfCalc.maxPts}</Tag>
                              </div>
                            </Col>
                            <Col span={6}>
                              <Text type="secondary">Revised Override Rating:</Text>
                              <div>
                                <Tag color="red" style={{ fontWeight: 'bold' }}>{selectedAppraisal.overrideRating} / {perfCalc.maxPts}</Tag>
                              </div>
                            </Col>
                            <Col span={6}>
                              <Text type="secondary">Modified By:</Text>
                              <div>
                                <Text strong style={{ color: '#1e293b' }}>
                                  {modifierName}
                                </Text>
                              </div>
                            </Col>
                            <Col span={6}>
                              <Text type="secondary">Audit Reason:</Text>
                              <div style={{ fontStyle: 'italic', color: '#595959' }}>
                                "{selectedAppraisal.overrideReason || 'N/A'}"
                              </div>
                            </Col>
                          </Row>

                          {/* Audit History Log if overrides exist */}
                          {overrideHistoryList.length > 0 && (
                            <div style={{ marginTop: '12px', paddingTop: '8px', borderTop: '1px dashed #ffa39e' }}>
                              <Text strong style={{ fontSize: '11px', color: '#cf1322' }}>Override History Trail ({overrideHistoryList.length} edit{overrideHistoryList.length > 1 ? 's' : ''}):</Text>
                              <Table
                                dataSource={overrideHistoryList}
                                rowKey={(r, i) => i}
                                pagination={false}
                                size="small"
                                bordered
                                style={{ marginTop: '6px' }}
                                columns={[
                                  { title: 'Original', dataIndex: 'originalRating', key: 'orig', render: (v) => `${(v !== null && v !== undefined && Number(v) > 0) ? Number(v).toFixed(2) : originalCalculatedRating} / ${perfCalc.maxPts}` },
                                  { title: 'Revised', dataIndex: 'revisedRating', key: 'rev', render: (v) => <Tag color="red">{v} / {perfCalc.maxPts}</Tag> },
                                  { title: 'Modified By', dataIndex: 'modifiedByName', key: 'by', render: (v) => v || modifierName },
                                  { title: 'Date & Time', dataIndex: 'modifiedDate', key: 'date', render: (d) => new Date(d).toLocaleString() },
                                  { title: 'Reason', dataIndex: 'reason', key: 'reason' },
                                ]}
                              />
                            </div>
                          )}
                        </Card>
                      );
                    })()}
                  </Card>

                  <Divider />

                  {/* Section 12 Manager Qualitative Feedback & Recommendations */}
                  <Title level={5} style={{ color: '#0f3b8c', marginBottom: '12px' }}>
                    2. Qualitative Assessment & Career Recommendation
                  </Title>

                  <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                      <Text strong style={{ color: '#1e293b' }}>Overall Manager Score (Max {maxPoints}):</Text>
                      <InputNumber
                        min={0}
                        max={maxPoints}
                        step={0.1}
                        style={{ width: '100%', marginTop: '4px' }}
                        value={overallManagerRating}
                        onChange={(val) => {
                          setOverallManagerRating(val);
                          setGoals((prev) =>
                            prev.map((g) =>
                              g.managerRating === null ||
                              g.managerRating === undefined ||
                              g.managerRating === '' ||
                              g.managerRating === overallManagerRating
                                ? { ...g, managerRating: val }
                                : g
                            )
                          );
                        }}
                      />
                    </Col>
                    <Col span={12}>
                      <Text strong style={{ color: '#1e293b' }}>Manager Recommendation (Doc Requirement):</Text>
                      <Select
                        style={{ width: '100%', marginTop: '4px' }}
                        value={recommendation}
                        onChange={(val) => setRecommendation(val)}
                      >
                        <Option value="Increment">Salary Increment Recommended</Option>
                        <Option value="Promotion">Promotion to Higher Role</Option>
                        <Option value="Confirmation">Probation Confirmation</Option>
                        <Option value="Retain">Retain in Current Role</Option>
                        <Option value="PIP">Place on Performance Improvement Plan (PIP)</Option>
                      </Select>
                    </Col>
                  </Row>

                  <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                      <Text strong style={{ color: '#1e293b' }}>Strengths (Doc Requirement):</Text>
                      <TextArea
                        rows={2}
                        style={{ marginTop: '4px' }}
                        value={strengths}
                        onChange={(e) => setStrengths(e.target.value)}
                        placeholder="List employee's key strengths and exceptional skills..."
                      />
                    </Col>
                    <Col span={12}>
                      <Text strong style={{ color: '#1e293b' }}>Weaknesses / Areas for Improvement (Doc Requirement):</Text>
                      <TextArea
                        rows={2}
                        style={{ marginTop: '4px' }}
                        value={weaknesses}
                        onChange={(e) => setWeaknesses(e.target.value)}
                        placeholder="List key weaknesses or areas requiring improvement..."
                      />
                    </Col>
                  </Row>

                  <Row gutter={16} style={{ marginBottom: '16px' }}>
                    <Col span={12}>
                      <Text strong style={{ color: '#1e293b' }}>Development Areas & Training Needs (Doc Requirement):</Text>
                      <TextArea
                        rows={2}
                        style={{ marginTop: '4px' }}
                        value={developmentAreas}
                        onChange={(e) => setDevelopmentAreas(e.target.value)}
                        placeholder="Specify training needs, courses, or development programs..."
                      />
                    </Col>
                    <Col span={12}>
                      <Text strong style={{ color: '#1e293b' }}>Overall Manager Comments & Summary:</Text>
                      <TextArea
                        rows={2}
                        style={{ marginTop: '4px' }}
                        value={overallManagerComments}
                        onChange={(e) => setOverallManagerComments(e.target.value)}
                        placeholder="Provide executive summary of employee performance..."
                      />
                    </Col>
                  </Row>

                  {/* Save / Submit Action Buttons */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
                    <Button
                      icon={<SaveOutlined />}
                      loading={saving}
                      onClick={() => handleSaveManagerEvaluation(false)}
                    >
                      Save Draft
                    </Button>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      loading={saving}
                      style={{ backgroundColor: '#0f3b8c' }}
                      onClick={() => handleSaveManagerEvaluation(true)}
                    >
                      Submit Manager Appraisal
                    </Button>
                  </div>
                </Card>
              )}
            </Col>
          </Row>

          {/* Reopen Modal */}
          <Modal
            title="Reopen Appraisal for Employee"
            open={reopenModalOpen}
            onCancel={() => setReopenModalOpen(false)}
            onOk={handleReopenAppraisal}
            confirmLoading={reopening}
            okText="Reopen & Unlock"
          >
            <Alert
              message="Reopening will unlock editing for the employee, allowing them to modify their self-appraisal."
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
            <Text strong>Reason for Reopening:</Text>
            <TextArea
              rows={3}
              style={{ marginTop: '6px' }}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              placeholder="Provide reason for reopening (e.g. Please update achievement figures for KPI 2)..."
            />
          </Modal>

          {/* HR Rating Override Modal (Section 17) */}
          <Modal
            title="17. HR Manager Rating Override (Audit Log)"
            open={overrideModalOpen}
            onCancel={() => setOverrideModalOpen(false)}
            onOk={handleSaveOverrideRating}
            confirmLoading={submittingOverride}
            okText="Apply Override & Record Audit"
            okButtonProps={{ danger: true }}
          >
            <Alert
              message="HR Override permissions allow revising calculated appraisal scores. Original rating, revised rating, modified by, date & mandatory reason will be permanently stored for audit compliance."
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />

            <Row gutter={16} style={{ marginBottom: '16px' }}>
              <Col span={12}>
                <Text type="secondary">System Calculated Rating:</Text>
                <div style={{ marginTop: '4px' }}>
                  <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px', fontWeight: 'bold' }}>
                    {perfCalc.finalScoreFormatted} / {perfCalc.maxPts} ({perfCalc.ratingCategory})
                  </Tag>
                </div>
              </Col>
              <Col span={12}>
                <Text strong style={{ color: '#cf1322' }}>Revised Override Rating (Max {perfCalc.maxPts}):</Text>
                <InputNumber
                  min={0}
                  max={perfCalc.maxPts}
                  step={0.1}
                  style={{ width: '100%', marginTop: '4px' }}
                  value={revisedRatingInput}
                  onChange={(val) => setRevisedRatingInput(val)}
                />
              </Col>
            </Row>

            <Form.Item label="Override Reason (Mandatory Audit Requirement)" required style={{ marginBottom: 0 }}>
              <TextArea
                rows={3}
                value={overrideReasonInput}
                onChange={(e) => setOverrideReasonInput(e.target.value)}
                placeholder="Specify justification for override (e.g. Adjusted score to 4.0 - Exceeds Expectations due to exceptional leadership during Q3 project execution)..."
              />
            </Form.Item>
          </Modal>

        </Content>
      </Layout>
    </Layout>
  );
}
